//@flow
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';

import LeafletPopup from 'common/components/core/leaflet/LeafletPopup';
import theme from './ZoneDetailsPopup.scss';
import { UserId } from 'common/models/internal/UserId';
import { ZoneMapVehicleId } from 'common/models/internal/ZoneMapVehicleId';
import { VehicleModelId } from 'common/models/internal/VehicleModelId';
import { userSelector } from 'common/redux/selectors/userSelector';
import { UserActions } from 'common/redux/actions/user';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import compose from 'recompose/compose';
import { withHandlers, lifecycle } from 'recompose';
import { List } from 'immutable';
import withPropsOnChange from 'recompose/withPropsOnChange';
import EventUtils from 'common/utils/EventUtils';
import ZoneDetailsPopupView from './ZoneDetailsPopupView';

const ZoneDetailsPopup = ({
    zone,
    folders,
    pincodes,
    isEditable,
    onDelete,
    enablePopupEditing,
    onClose,
    onNameChange,
    onRouteIdChange,
    onDescriptionChange,
    onLocalitiesChange,
    onPincodesChange,
    onFolderChange,
    onStyleChange,
    selectedFolder,
    enableMapEvents,
    disableMapEvents,
    onRidersChange,
    availableRiders,
    zoneType,
    availableVehicles,
    availableVehicleModels,
    onVehiclesChange,
    onVehicleModelsChange,
    mapMetadataSettings
}) => (
    <div className={theme.popupWrap}>
        <LeafletPopup className={theme.zoneDetailsPopupWrap} closeOnEscapeKey>
            <ZoneDetailsPopupView
                zoneType={zoneType}
                isEditable={isEditable}
                zone={zone}
                selectedFolder={selectedFolder}
                folders={folders}
                pincodes={pincodes}
                onClose={onClose}
                onNameChange={onNameChange}
                onLocalitiesChange={onLocalitiesChange}
                onPincodesChange={onPincodesChange}
                onFolderChange={onFolderChange}
                onDescriptionChange={onDescriptionChange}
                onRouteIdChange={onRouteIdChange}
                onDelete={onDelete}
                onStyleChange={onStyleChange}
                enablePopupEditing={enablePopupEditing}
                enableMapEvents={enableMapEvents}
                disableMapEvents={disableMapEvents}
                onRidersChange={onRidersChange}
                availableRiders={availableRiders}
                availableVehicles={availableVehicles}
                availableVehicleModels={availableVehicleModels}
                onVehiclesChange={onVehiclesChange}
                onVehicleModelsChange={onVehicleModelsChange}
                mapMetadataSettings={mapMetadataSettings}
            />
        </LeafletPopup>
    </div>
);

const Handlers = withHandlers({
    onClose: ({
        handlePopupClose,
        setDeleteConfirmationDialogInactive
    }) => e => {
        EventUtils.stopPropagation(e);
        setDeleteConfirmationDialogInactive();
        handlePopupClose();
    },
    onChange: ({ zone, onUpdateGeometry }) => updatedProperties => {
        const [updatedProperty] = Object.keys(updatedProperties);
        const updatedZone =
            updatedProperty &&
            zone.set(updatedProperty, updatedProperties[updatedProperty]);
        updatedZone && onUpdateGeometry(updatedZone);
    },
    onNameChange: ({ zone, onUpdateGeometry }) => value => {
        onUpdateGeometry(zone.setName(value));
    },
    onDescriptionChange: ({ zone, onUpdateGeometry }) => value => {
        onUpdateGeometry(zone.setDescription(value));
    },
    onLocalitiesChange: ({ zone, onUpdateGeometry }) => value => {
        onUpdateGeometry(zone.setLocalities(List(value)));
        return Promise.resolve();
    },
    onStyleChange: ({ zone, onUpdateGeometry }) => styles => {
        styles && onUpdateGeometry(zone.setStyles(styles));
    },
    onFolderChange: ({ zone, onUpdateGeometry }) => e => {
        const id = e ? e.id : null;
        onUpdateGeometry(zone.setDirectoryId(id), { sort: true });
    },
    onPincodesChange: ({ zone, onUpdateGeometry }) => value => {
        onUpdateGeometry(zone.setPincodes(List(value)));
        return Promise.resolve();
    },
    onRidersChange: ({ clientId, zone, onUpdateGeometry }) => values => {
        const updatedRiders =
            values?.map(value => UserId.fromJS({ clientId, userId: value })) ||
            [];
        onUpdateGeometry(zone.setUsers(List(updatedRiders)));
        return Promise.resolve();
    },
    onVehiclesChange: ({ clientId, zone, onUpdateGeometry }) => values => {
        const updatedVehicles =
            values?.map(value =>
                ZoneMapVehicleId.fromJS({ clientId, id: value })
            ) || [];
        onUpdateGeometry(zone.setVehicles(List(updatedVehicles)));
        return Promise.resolve();
    },
    onVehicleModelsChange: ({ clientId, zone, onUpdateGeometry }) => values => {
        const updatedVehicleModels =
            values?.map(value =>
                VehicleModelId.fromJS({ clientId, modelId: value })
            ) || [];
        onUpdateGeometry(zone.setVehicleModels(List(updatedVehicleModels)));
        return Promise.resolve();
    },
    onRouteIdChange: ({ zone, onUpdateGeometry }) => routeId => {
        onUpdateGeometry(zone.setRouteId(routeId));
    },
    enablePopupEditing: ({ setZoneEditable }) => () => {
        setZoneEditable();
    }
});

const actions = {
    fetchRiders: UserActions.fetchUsersByClientId
};

const mapStateToProps = (state, props) => {
    const clientId = clientIdSelector(state, props);
    const availableRiders = userSelector(state, props);
    return {
        clientId,
        availableRiders
    };
};

const dataFetcher = {
    UNSAFE_componentWillMount() {
        const { fetchRiders, clientId, teamId } = this.props;
        fetchRiders(clientId, null, null, [teamId]);
    }
};
const DerivedProps = withPropsOnChange(['zone'], ({ zone, folders }) => {
    const selectedFolders = folders.filter(
        folder => folder.id === zone.directoryId
    );
    const selectedFolder = selectedFolders.size
        ? selectedFolders.get(0).toJS()
        : undefined;
    return { selectedFolder };
});

export default compose(
    withRouter,
    connect(mapStateToProps, actions),
    lifecycle(dataFetcher),
    Handlers,
    DerivedProps
)(ZoneDetailsPopup);
